<template>
  <div class="version-timeline-wrapper mb-8">
    <div class="timeline-dot-container">
      <div :class="['timeline-dot', isLatest ? 'dot-active' : '']"></div>
    </div>

    <v-card outlined rounded="xl" class="release-card elevation-2 white">
      <div class="pa-5 border-bottom bg-gradient-header rounded-top-xl">
        <div class="d-flex align-center justify-space-between flex-wrap ga-3">
          <div class="d-flex align-center flex-wrap chips-gap-container">
            <div
              class="version-badge-gradient px-4 py-1.5 rounded-xl text-h6 font-weight-black white--text shadow-sm mr-3 my-1"
            >
              <v-icon left small color="white">mdi-tag-outline</v-icon>
              {{ release.versao }}
            </div>

            <v-chip
              v-if="isLatest"
              color="amber darken-3"
              dark
              small
              class="font-weight-black pulse-badge elevation-1 mr-3 my-1"
            >
              <v-icon left x-small>mdi-sparkles</v-icon>
              Versão Atual
            </v-chip>

            <v-chip
              color="var(--brand-soft)"
              text-color="var(--brand)"
              small
              class="font-weight-bold mr-3 my-1"
            >
              <v-icon left x-small color="var(--brand)">mdi-source-commit</v-icon>
              {{ release.total_commits || totalCommitsCount }} commits nesta versão
            </v-chip>

            <v-chip
              color="var(--vc-tint-azul)"
              text-color="var(--vc-tint-azul-texto)"
              small
              class="font-weight-bold mr-3 my-1"
            >
              <v-icon left x-small color="var(--vc-tint-azul-texto)">mdi-source-branch</v-icon>
              Branch: {{ release.branch || "main" }}
            </v-chip>
          </div>

          <div class="d-flex align-center release-date-pill px-3 py-1.5 rounded-lg my-1">
            <v-icon small color="var(--text-3)" class="mr-1">mdi-calendar-check-outline</v-icon>
            <span class="text-caption font-weight-bold text-slate">
              {{ release.data_release_formatada || "Data a definir" }}
            </span>
          </div>
        </div>
      </div>

      <v-card-text class="pa-6">
        <div v-if="!hasCommits" class="pa-6 text-center grey--text text--darken-1 font-italic">
          Nenhum commit detalhado registrado para esta versão.
        </div>

        <div v-else class="version-commits-container">
          <div v-for="(commitsGroup, catKey) in groupedCommits" :key="catKey" class="mb-5">
            <div v-if="commitsGroup && commitsGroup.length > 0">
              <div class="d-flex align-center mb-3">
                <category-badge :tipo="catKey" />
                <div class="flex-grow-1 ml-3 category-divider"></div>
              </div>

              <div class="pl-2">
                <commit-item
                  v-for="commit in commitsGroup"
                  :key="commit.id || commit.hash"
                  :commit="commit"
                />
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import CategoryBadge from "./CategoryBadge.vue";
import CommitItem from "./CommitItem.vue";

export default {
  name: "VersionCard",
  components: {
    CategoryBadge,
    CommitItem,
  },
  props: {
    release: {
      type: Object,
      required: true,
    },
    isLatest: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasCommits() {
      if (this.release.commits && this.release.commits.length > 0) return true;
      if (this.release.commits_grouped) {
        return Object.values(this.release.commits_grouped).some(
          (arr) => Array.isArray(arr) && arr.length > 0
        );
      }
      return false;
    },
    totalCommitsCount() {
      if (this.release.commits && Array.isArray(this.release.commits)) {
        return this.release.commits.length;
      }
      return 0;
    },
    groupedCommits() {
      if (this.release.commits_grouped) {
        return this.release.commits_grouped;
      }
      const groups = {
        feat: [],
        fix: [],
        refactor: [],
        style: [],
        chore: [],
      };
      if (this.release.commits && Array.isArray(this.release.commits)) {
        this.release.commits.forEach((c) => {
          const type = (c.tipo || "feat").toLowerCase();
          if (["feat", "feature", "recurso"].includes(type)) groups.feat.push(c);
          else if (["fix", "bug", "correcao"].includes(type)) groups.fix.push(c);
          else if (["refactor", "refatoracao", "perf"].includes(type)) groups.refactor.push(c);
          else if (["style", "ui", "estilo"].includes(type)) groups.style.push(c);
          else groups.chore.push(c);
        });
      }
      return groups;
    },
  },
};
</script>

<style scoped>
.version-timeline-wrapper {
  --vc-linha: #cbd5e1;
  --vc-tint-azul: #e3f2fd;
  --vc-tint-azul-texto: #1d4ed8;
  position: relative;
  padding-left: 28px;
}

.theme--dark .version-timeline-wrapper {
  --vc-linha: #4a4458;
  --vc-tint-azul: rgba(59, 130, 246, 0.16);
  --vc-tint-azul-texto: #93c5fd;
}

.version-timeline-wrapper::before {
  content: "";
  position: absolute;
  left: 9px;
  top: 24px;
  bottom: -32px;
  width: 3px;
  background: linear-gradient(180deg, var(--vc-linha) 0%, var(--border) 100%);
  border-radius: 2px;
}

.version-timeline-wrapper:last-child::before {
  display: none;
}

.timeline-dot-container {
  position: absolute;
  left: 0;
  top: 20px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.timeline-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: var(--vc-linha);
  border: 3px solid var(--surface);
  box-shadow: 0 0 0 2px var(--text-faint);
}

.dot-active {
  background-color: #4f008f;
  box-shadow: 0 0 0 3px rgba(79, 0, 143, 0.3);
}

.release-card {
  border-color: var(--border) !important;
  border-left: 4px solid var(--brand) !important;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.release-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.09) !important;
}

.bg-gradient-header {
  background: linear-gradient(135deg, var(--surface) 0%, var(--surface-2) 100%);
  border-bottom: 1px solid var(--border-soft);
}

.version-badge-gradient {
  background: linear-gradient(135deg, #4f008f 0%, #31005a 100%);
  display: inline-flex;
  align-items: center;
  letter-spacing: 0.02em;
}

.release-date-pill {
  background-color: var(--surface-3);
  border: 1px solid var(--border);
}

.text-slate {
  color: var(--text-2);
}

.category-divider {
  height: 1px;
  background: linear-gradient(90deg, var(--vc-linha) 0%, var(--border-soft) 100%);
}

.pulse-badge {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(217, 119, 6, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(217, 119, 6, 0);
  }
}
</style>
